import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// In production with custom domain (vywork.com), use relative URLs to avoid CORS.
// Check if current origin matches the configured backend URL - if not, use relative paths.
const getBaseURL = () => {
  if (typeof window !== "undefined" && API_URL) {
    try {
      const backendHost = new URL(API_URL).host;
      const currentHost = window.location.host;
      // If frontend domain differs from backend URL (e.g. custom domain), use relative URLs
      if (backendHost !== currentHost) {
        return "";
      }
    } catch (e) {
      // URL parsing failed, use relative
      return "";
    }
  }
  return API_URL;
};

// Create axios instance - uses relative URLs when on custom domain
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("token");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Setup axios interceptor for auth header
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem("token");
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const verifyToken = useCallback(async (currentToken) => {
    if (!currentToken) {
      setLoading(false);
      setInitialized(true);
      return;
    }
    
    try {
      const response = await api.get("/api/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error);
      try {
        localStorage.removeItem("token");
      } catch {}
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    verifyToken(token);
  }, [token, verifyToken]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await api.post("/api/auth/login", { email, password });
      const { token: newToken, user: userData } = response.data;
      try {
        localStorage.setItem("token", newToken);
      } catch {}
      setToken(newToken);
      setUser(userData);
      return userData;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    setLoading(true);
    try {
      const response = await api.post("/api/auth/register", { email, password, name });
      const { token: newToken, user: userData } = response.data;
      try {
        localStorage.setItem("token", newToken);
      } catch {}
      setToken(newToken);
      setUser(userData);
      return userData;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem("token");
    } catch {}
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    initialized,
    isAuthenticated: !!token && !!user,
    login,
    register,
    logout,
    getAuthHeader,
    api,
    setUser,
    setToken
  }), [user, token, loading, initialized, getAuthHeader]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Export api instance for use in components
export { api };
